import { useCallback, useMemo, useState } from 'react'
import { notification } from 'antd'
import {
  useBillingProfilesQuery,
  useBillingProfileStatsQuery,
  useToggleBillingProfileStatusMutation,
} from '../../api/client-billing-profile-api-client'
import { getAllBillingProfiles } from '../../api/client-billing-profile.api'
import { BillingProfileRecord } from '../../api/dto/client-billing-profile.dto'
import { extractError } from '../../api/extract-error'
import {
  downloadProfilesExport,
  type ExportFormat,
  type ProfileExportScope,
} from './bulkUpload.excel'
import { BillingProfileStatus } from './types'
import { useBillingProfileDialogs } from './useBillingProfileDialogs'
import { useBillingProfileFilters } from './useBillingProfileFilters'

export const useBillingProfileTable = () => {
  const filters = useBillingProfileFilters()
  const [isExporting, setIsExporting] = useState(false)

  // flashRow stays internal: the list only needs the id it flashed.
  const { flashRow, ...dialogs } = useBillingProfileDialogs()
  const { handleCloseExport } = dialogs

  const { data, isLoading, isError } = useBillingProfilesQuery({
    ...filters.listFilters,
    page: filters.page,
    limit: filters.pageSize,
  })

  const { data: statsData } = useBillingProfileStatsQuery(filters.statsFilters)

  const records = data?.data ?? []
  const total = data?.total ?? 0

  const toggleStatusMutation = useToggleBillingProfileStatusMutation({
    onSuccess: (updated) => {
      const verb =
        updated.status === BillingProfileStatus.Active ? 'restored' : 'removed'
      notification.success({
        message: `Company ${verb} successfully`,
      })
      flashRow(updated.id)
    },
    onError: (error) => {
      notification.error({
        message: extractError(error, 'Failed to update status'),
      })
    },
  })

  const stats = useMemo(
    () => ({
      total: statsData?.total ?? 0,
      active: statsData?.active ?? 0,
      inactive: statsData?.inactive ?? 0,
    }),
    [statsData]
  )

  /** Exports every profile matching the filters, in the list's order, not just this page. */
  const handleExport = async (
    scope: ProfileExportScope,
    format: ExportFormat
  ) => {
    setIsExporting(true)
    try {
      downloadProfilesExport(
        await getAllBillingProfiles(filters.listFilters),
        scope,
        format
      )
      handleCloseExport()
    } catch {
      notification.error({ message: 'Failed to export billing profiles' })
    } finally {
      setIsExporting(false)
    }
  }

  const handleToggleStatus = useCallback(
    (record: BillingProfileRecord) => {
      const newStatus =
        record.status === BillingProfileStatus.Active
          ? BillingProfileStatus.Inactive
          : BillingProfileStatus.Active
      toggleStatusMutation.mutate({
        id: record.id,
        body: { status: newStatus },
      })
    },
    [toggleStatusMutation.mutate]
  )

  /**
   * Awaited, because removing the company is what the dialog's offer to strip
   * leftover roles runs after. False leaves the dialog open on a failure.
   */
  const handleConfirmRemove = useCallback(
    async (record: BillingProfileRecord) => {
      try {
        await toggleStatusMutation.mutateAsync({
          id: record.id,
          body: { status: BillingProfileStatus.Inactive },
        })
        return true
      } catch {
        // The mutation reports the failure; the dialog stays put.
        return false
      }
    },
    [toggleStatusMutation.mutateAsync]
  )

  return {
    ...dialogs,
    ...filters,
    records,
    total,
    isLoading,
    isError,
    stats,
    isExporting,
    handleExport,
    handleToggleStatus,
    handleConfirmRemove,
  }
}
